import React, { useState, useEffect, useMemo } from 'react';
import { TrashIcon } from '@heroicons/react/solid';
import { getAllowedScenarios, isMasterAdmin } from '../config/accessControl';
import { trackVisit } from '../services/analytics';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CompanySelector = ({ companies, selectedCompany, onSelectCompany, onAddNewScenario, addButtonText, onDeleteCompany, permanentlyDeleteCompany, userEmail }) => {
  const [newScenarioName, setNewScenarioName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [allowedScenarios, setAllowedScenarios] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userCurrentCompany, setUserCurrentCompany] = useState('');

  useEffect(() => {
    const fetchUserCompany = async () => {
      if (!userEmail) return;
      
      try {
        const userRef = doc(db, 'userAccess', userEmail);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.to_dict();
          const currentScenario = userData.currentScenario;
          
          if (currentScenario) {
            setUserCurrentCompany(currentScenario);
            
            if (!selectedCompany) {
              onSelectCompany(currentScenario);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user company:', error);
      }
    };
    
    fetchUserCompany();
  }, [userEmail, selectedCompany, onSelectCompany]);

  useEffect(() => {
    const fetchAllowedScenarios = async () => {
      if (userEmail) {
        const scenarios = await getAllowedScenarios(userEmail);
        setAllowedScenarios(scenarios);
      }
    };
    fetchAllowedScenarios();
  }, [userEmail]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (userEmail) {
        const adminStatus = await isMasterAdmin(userEmail);
        setIsAdmin(adminStatus);
      }
    };
    checkAdminStatus();
  }, [userEmail]);

  const handleAddScenario = () => {
    if (newScenarioName) {
      onAddNewScenario(newScenarioName);
      setNewScenarioName('');
      setShowAddForm(false);
    }
  };

  const handleDeleteCompany = (e, company) => {
    e.preventDefault();
    e.stopPropagation();
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (type) => {
    if (type === 'permanent') {
      permanentlyDeleteCompany(companyToDelete);
    } else if (type === 'soft') {
      onDeleteCompany(companyToDelete);
    }
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  };

  const handleSelectCompany = async (company) => {
    if (!company) return;
    
    console.log(`Company selected: ${company}`);
    
    onSelectCompany(company);
    
    setUserCurrentCompany(company);
  };

  const filteredCompanies = useMemo(() => {
    if (isMasterAdmin(userEmail) || allowedScenarios === 'all') return companies;
    return companies.filter(company => allowedScenarios.includes(company));
  }, [companies, userEmail, allowedScenarios]);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        <div className="relative flex-grow">
          <select
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-700"
            value={selectedCompany}
            onChange={(e) => handleSelectCompany(e.target.value)}
          >
            <option value="">Select a scenario</option>
            {filteredCompanies.map((company) => (
              <option 
                key={company} 
                value={company} 
                className={company === userCurrentCompany ? 'font-bold' : ''}
              >
                {company}
              </option>
            ))}
          </select>
        </div>
        {isAdmin && (
          <>
            {selectedCompany && (
              <button
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors duration-200"
                onClick={(e) => handleDeleteCompany(e, selectedCompany)}
                title="Delete Company"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            )}
          </>
        )}
      </div>
      {!showAddForm && isAdmin && (
        <button
          className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 transition duration-300"
          onClick={() => setShowAddForm(true)}
        >
          {addButtonText}
        </button>
      )}
      {showAddForm && (
        <div className="space-y-2">
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="New scenario name"
            value={newScenarioName}
            onChange={(e) => setNewScenarioName(e.target.value)}
          />
          <div className="flex space-x-2">
            <button
              className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 transition duration-300"
              onClick={handleAddScenario}
            >
              Add
            </button>
            <button
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition duration-300"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Delete {companyToDelete}?
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  How would you like to delete this company?
                </p>
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={() => handleDeleteConfirm('permanent')}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-200"
                >
                  Delete Permanently
                </button>
                <button
                  onClick={() => handleDeleteConfirm('soft')}
                  className="w-full px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition duration-200"
                >
                  Soft Delete (Can be restored)
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setCompanyToDelete(null);
                  }}
                  className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;